import React, { useState, useEffect } from "react";
import { Link, useParams } from "react-router-dom";
import { useCollection } from "../useData";
import { resolveImg } from "../api";
import SEO from "../components/SEO";
import NotFound from "../components/NotFound";

const ProjectDetail = () => {
  const { id } = useParams();
  const projects = useCollection("projects") || [];
  const project = projects.find((p) => p.id === id);
  const [idx, setIdx] = useState(0);

  // Reset image index whenever the user navigates to a different project.
  useEffect(() => { setIdx(0); }, [id]);

  // Still loading collection — render nothing (avoid flash of 404).
  if (!projects.length) return null;
  if (!project) return <NotFound section="This project" backTo="/projects" backLabel="Back to projects" />;

  const images = project.images || [];
  const next = () => images.length && setIdx((i) => (i + 1) % images.length);
  const prev = () => images.length && setIdx((i) => (i - 1 + images.length) % images.length);

  const currentIdx = projects.findIndex((p) => p.id === project.id);
  const nextProject = projects[(currentIdx + 1) % projects.length];

  return (
    <div className="px-6 md:px-10 max-w-[1100px] mx-auto">
      <SEO
        title={project.title}
        description={project.description || `${project.title} — ${project.category || "project"} by Alice Lisi.`}
        image={project.cover || images[0]}
        path={`/projects/${project.id}`}
      />

      {/* Riccardo Svelto style — photo centered on paper with natural aspect */}
      <div className="max-w-[900px] mx-auto">
        <div className="relative w-full flex items-center justify-center bg-[color:var(--paper)] fade-in" style={{ height: "75vh" }}>
          {images.map((src, i) => (
            <img
              key={i}
              src={resolveImg(src)}
              alt={`${project.title} ${i + 1}`}
              className={`absolute max-h-full max-w-full object-contain transition-opacity duration-700 ${i === idx ? "opacity-100" : "opacity-0"}`}
            />
          ))}
          <button onClick={prev} className="absolute left-0 top-0 h-full w-1/2 z-10" aria-label="Previous" style={{ cursor: "w-resize" }} />
          <button onClick={next} className="absolute right-0 top-0 h-full w-1/2 z-10" aria-label="Next" style={{ cursor: "e-resize" }} />
        </div>

        <div className="mt-4 flex items-center justify-center">
          <div className="flex gap-2">
            {images.map((_, i) => (
              <button
                key={i}
                onClick={() => setIdx(i)}
                className={`w-1.5 h-1.5 rounded-full transition-colors ${i === idx ? "bg-[color:var(--ink)]" : "bg-[color:var(--line)]"}`}
                aria-label={`Image ${i + 1}`}
              />
            ))}
          </div>
        </div>
      </div>

      {/* About — no label */}
      <div className="mt-20 max-w-[900px] mx-auto">
        <p className="font-serif text-2xl md:text-3xl leading-[1.35]">{project.description}</p>
      </div>

      {/* Credits — no label */}
      <div className="mt-16 max-w-[900px] mx-auto pb-4">
        <dl className="grid grid-cols-12 gap-y-2 font-serif">
          <dt className="col-span-4 text-[color:var(--ink-mute)]">Year</dt><dd className="col-span-8">{project.year}</dd>
          <dt className="col-span-4 text-[color:var(--ink-mute)]">Type</dt><dd className="col-span-8">{project.category}</dd>
          <dt className="col-span-4 text-[color:var(--ink-mute)]">Photographer</dt><dd className="col-span-8">Alice Lisi</dd>
          <dt className="col-span-4 text-[color:var(--ink-mute)]">Frames</dt><dd className="col-span-8 counter">{images.length}</dd>
        </dl>
      </div>

      {/* Next */}
      <div className="mt-32 pt-8 border-t border-[color:var(--line)] flex items-baseline justify-between">
        <span className="font-serif italic text-[color:var(--ink-mute)]">Next project</span>
        <Link to={`/projects/${nextProject.id}`} className="font-serif italic text-2xl tlink">{nextProject.title} →</Link>
      </div>
    </div>
  );
};

export default ProjectDetail;
